import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Button, Popover } from "antd";
import { QuestionCircleOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import { textWidgetExtensions } from "./tiptapExtensions";
import { EditorToolbar } from "./EditorToolbar";
import { useTiptapSlicerMenu } from "./useTiptapSlicerMenu";
import { VariableSyntaxHelp } from "../shared/VariableSyntaxHelp";
import { SlicerTokenMenu } from "../shared/SlicerTokenMenu";
import { useSlicerVariables } from "../../Contexts/dashboardHooks";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import { sanitizeWidgetHtml } from "../../Utils/sanitizeHtml";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Fires when the `{Slicer.` menu opens or closes. */
  onMenuOpenChange?: (open: boolean) => void;
}

// WYSIWYG editor for the text widget. The editor is the source of truth for
// content; it emits HTML on every change. `value` seeds the initial document
// once and is not synced back afterwards. The Preview toggle renders a read-only
// view with template variables resolved, without mutating the editor.
export function RichTextEditor({ value, onChange, onMenuOpenChange }: RichTextEditorProps) {
  const [preview, setPreview] = useState(false);
  // Seeded once: a new `content` on every render would make useEditor reset its options.
  const [initialContent] = useState(() => value);
  const onChangeRef = useRef(onChange);
  const onMenuOpenChangeRef = useRef(onMenuOpenChange);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    onMenuOpenChangeRef.current = onMenuOpenChange;
  });

  const slicerValues = useSlicerVariables();
  const { editorProps, attach, trigger, wrapperRef, anchorStyle } = useTiptapSlicerMenu();

  const editor = useEditor({
    extensions: textWidgetExtensions,
    content: initialContent,
    editorProps,
    onUpdate: ({ editor }) => onChangeRef.current(editor.getHTML()),
  });

  useEffect(() => attach(editor), [editor, attach]);

  // Preview unmounts EditorContent and detaches the view DOM, so the menu closes.
  const { close } = trigger;
  useEffect(() => {
    if (preview) close();
  }, [preview, close]);

  const menuOpen = trigger.menu.open;
  useEffect(() => {
    onMenuOpenChangeRef.current?.(menuOpen);
  }, [menuOpen]);

  function insertVariable(tokenText: string) {
    trigger.insert(tokenText);
  }

  const resolvedPreview = preview
    ? sanitizeWidgetHtml(resolveTemplate(editor?.getHTML() ?? "", slicerValues, { escapeHtml: true }).resolved)
    : "";

  const { menu, model, listId } = trigger;

  return (
    <div className="rte">
      <div className="rte-toolbar-row">
        <div className="rte-toolbar-left">
          {preview
            ? <span className="rte-preview-label">Preview mode</span>
            : <EditorToolbar editor={editor} />}
        </div>
        <div className="rte-toolbar-right">
          {!preview && (
            <Popover
              content={<VariableSyntaxHelp onInsert={insertVariable} />}
              trigger="click"
              placement="bottomRight"
            >
              <span className="rte-var-btn" title="Insert dynamic variable">
                <QuestionCircleOutlined /> Variables
              </span>
            </Popover>
          )}
          <Button
            size="small"
            type="text"
            className={preview ? "rte-toggle-btn is-active" : "rte-toggle-btn"}
            icon={preview ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => setPreview((p) => !p)}
          >
            {preview ? "Edit" : "Preview"}
          </Button>
        </div>
      </div>

      {preview
        ? (
          <div
            className="rte-content rte-preview text-widget-content"
            dangerouslySetInnerHTML={{ __html: resolvedPreview }}
          />
        )
        : (
          <div className="rte-content-wrap" ref={wrapperRef}>
            <EditorContent editor={editor} className="rte-content" onScroll={close} />
            <SlicerTokenMenu
              open={menu.open && anchorStyle != null}
              model={model}
              activeIndex={menu.open ? menu.activeIndex : 0}
              listId={listId}
              onPick={(index) => trigger.pick(index)}
              onHover={trigger.setActive}
            >
              <span className="rte-slicer-anchor" style={anchorStyle ?? undefined} aria-hidden="true" />
            </SlicerTokenMenu>
          </div>
        )
      }
    </div>
  );
}
